import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import ScopeField from '../../components/ScopeField';
import {
  createMaryajRatioRule, deleteMaryajRatioRule, listMaryajRatioRules, updateMaryajRatioRule,
} from '../../api/maryajRatio';
import './KonbinezonRatioPage.css';

// RÈG 4 — Pousantaj sou Maryaj. Quand ON, le nombre de mariages DISTINCTS d'une
// fiche est plafonné à round(N(N-1)/2 × P / 100), N = numéros BOR distincts sur la
// fiche (paires incluses), arrondi 0.5 vers le haut. OFF → aucune limite.
// Portée company (Tout) / group / bank — le plus spécifique gagne côté vente.

const PCT_OPTIONS = [50, 70, 80, 90, 100];
const DEFAULTS = { enabled: false, allowed_pct: 70 };

const scopeParams = (filter, scopeId) => {
  if (filter === 'Groupe') return { group: scopeId };
  if (filter === 'Banque') return { bank: scopeId };
  return {};
};

const possibles = (n) => (n * (n - 1)) / 2;
const capFor = (n, pct) => Math.round((possibles(n) * pct) / 100);

const MaryajRatioPage = () => {
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');

  const [ruleId, setRuleId] = useState(null);
  const [enabled, setEnabled] = useState(DEFAULTS.enabled);
  const [pct, setPct] = useState(DEFAULTS.allowed_pct);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const scopeReady = filter === 'Tout' || !!scopeId;

  const load = useCallback(() => {
    if (!scopeReady) return;
    setLoading(true);
    setError('');
    setSaved(false);
    listMaryajRatioRules(scopeParams(filter, scopeId))
      .then((rows) => {
        const rule = Array.isArray(rows) ? rows[0] : null;
        if (rule) {
          setRuleId(rule.id);
          setEnabled(rule.enabled);
          setPct(Number(rule.allowed_pct));
        } else {
          setRuleId(null);
          setEnabled(DEFAULTS.enabled);
          setPct(DEFAULTS.allowed_pct);
        }
      })
      .catch(() => setError('Impossible de charger la règle.'))
      .finally(() => setLoading(false));
  }, [filter, scopeId, scopeReady]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    const payload = { enabled, allowed_pct: pct };
    try {
      if (ruleId) {
        await updateMaryajRatioRule(ruleId, payload);
      } else {
        const created = await createMaryajRatioRule({ ...payload, ...scopeParams(filter, scopeId) });
        setRuleId(created.id);
      }
      setSaved(true);
    } catch (err) {
      setError('Sauvegarde impossible. Vérifiez les valeurs.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!ruleId) return;
    setSaving(true);
    setError('');
    try {
      await deleteMaryajRatioRule(ruleId);
      setRuleId(null);
      setEnabled(DEFAULTS.enabled);
      setPct(DEFAULTS.allowed_pct);
      setSaved(false);
    } catch (err) {
      setError('Suppression impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <p className="kr-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/securite" className="bc-link">Securite</Link>
        {' >> '}
        <span className="bc-current">Pousantaj sou Maryaj</span>
      </p>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Pousantaj sou Maryaj (RÈG 4)</span>
        </div>
        <div className="kr-card-body">
          <div className="kr-radio-row">
            {['Tout', 'Groupe', 'Banque'].map((opt) => (
              <label key={opt} className="kr-radio-label">
                <input
                  type="radio"
                  name="mr-filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="kr-row">
              <label className="kr-label">{filter}</label>
              {filter === 'Banque' ? (
                <BankScopeField
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, name) => { setScopeId(id); setScopeName(name); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              ) : (
                <ScopeField
                  filter={filter}
                  id={scopeId}
                  name={scopeName}
                  onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                  inputClass="kr-input kr-input-sm"
                  iconClass="kr-icon-btn"
                  nameClass="kr-scope-name"
                  errClass="kr-lookup-err"
                />
              )}
            </div>
          )}

        </div>
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">
            Règle {filter === 'Tout' ? 'générale (company)' : filter.toLowerCase()}
          </span>
        </div>

        {!scopeReady ? (
          <div className="kr-card-body">
            <p className="kr-hint">Choisir un {filter.toLowerCase()} ci-dessus.</p>
          </div>
        ) : loading ? (
          <div className="kr-card-body"><p>Chargement...</p></div>
        ) : (
          <form className="kr-card-body" onSubmit={handleSave}>
            <label className="kr-toggle-row">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => { setEnabled(e.target.checked); setSaved(false); }}
              />
              <span>Aktive règ la</span>
              {!ruleId && <span className="kr-hint">(pas encore de règle pour cette portée)</span>}
            </label>

            <div className="kr-row">
              <label className="kr-label">Pousantaj</label>
              <select
                className="kr-input"
                value={pct}
                disabled={!enabled}
                onChange={(e) => { setPct(Number(e.target.value)); setSaved(false); }}
              >
                {PCT_OPTIONS.map((p) => <option key={p} value={p}>{p} %</option>)}
              </select>
              <span className="kr-hint">des mariages possibles d'une fiche</span>
            </div>

            <div className="kr-btn-row">
              <button type="submit" className="kr-save-btn" disabled={saving}>
                {ruleId ? 'Anrejistre' : 'Kreye règ la'}
              </button>
              {ruleId && (
                <button type="button" className="kr-del-btn" onClick={handleDelete} disabled={saving}>
                  Efase
                </button>
              )}
            </div>

            {saved && <p className="kr-ok">Konfigirasyon anrejistre.</p>}
            {error && <p className="kr-error">{error}</p>}
          </form>
        )}
      </div>

      <div className="kr-card">
        <div className="kr-card-header">
          <span className="kr-card-title">Kijan sa mache</span>
        </div>
        <div className="kr-card-body">
          <p className="kr-explain">
            Kantite maryaj <strong>posib</strong> yon fich = <strong>N × (N − 1) ÷ 2</strong>,
            kote N = kantite nimewo Borlette <strong>diferan</strong> sou fich la (10 pè yo
            konte tou). Kantite maryaj <strong>pèmèt</strong> = arondi (posib × {pct} %).
          </p>
          <table className="kr-mini-table">
            <thead>
              <tr><th>N boul</th><th>Maryaj posib</th><th>Pèmèt ({pct} %)</th></tr>
            </thead>
            <tbody>
              {[4, 5, 6, 7, 8].map((n) => (
                <tr key={n}>
                  <td>{n}</td>
                  <td>{possibles(n)}</td>
                  <td>{capFor(n, pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="kr-explain kr-explain-note">
            Pa gen mwatye maryaj (arondi 0,5 anlè). Si fich la depase kantite pèmèt la →
            bloke. Si RÈG 4 OFF → pa gen limit, tout maryaj posib yo itilizab.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default MaryajRatioPage;
